import { useCallback, useMemo, useRef, useState } from "react"
import { LocationResult } from "./types"
import { setMapSearchIsActive, setSearchCoordinate } from "./slice"
import { useAppDispatch } from "integrations/redux/hooks"
import { config } from "@nova/configuration/browser"
import { mapSearchIcon } from "./constants"
import * as Icons from "@nova/component-icons"
import { LngLatCoordinatePair } from "../event/types"
import { getGlobalTerrainData } from "../GlobalTerrainManager"
import { searchRegisteredEntities } from "plugins/plugin-registry"
import { setNewViewstate } from "../GlobalViewState"
import { setFollowingEntity } from "plugins/shared/playback/state/slice"
import { parseCoordinate } from "@nova/calculate-geospatial"
import { convertFromW3W, w3wAddressPattern } from "./convertFromW3W"

export const useMapSearch = () => {
    const dispatch = useAppDispatch()

    const [searchContents, setSearchContents] = useState<string>()
    const abortController = useRef(new AbortController())
    const latestQuery = useRef<string>(undefined)
    const [coordinateResult, setCoordinateResult] = useState<LocationResult>()
    const [entityResults, setEntityResults] = useState<LocationResult[]>([])
    const [geoResults, setGeoResults] = useState<LocationResult[]>([])

    const searchGeocode = useCallback(async (searchQuery: string) => {
        try {
            const response = await fetch(
                `https://api.maptiler.com/geocoding/${encodeURIComponent(
                    searchQuery
                )}.json?key=${config.get("mapTilerApiKey")}&limit=5&proximity=ip`,
                { signal: abortController.current.signal }
            )
            const data = await response.json()
            setGeoResults(
                data.features.map((feature: Record<string, unknown>) => {
                    const parts = (feature.place_name as string)
                        .split(",")
                        .map((part) => part.trim())

                    const result = {
                        title: parts[0] || "",
                        subtitle: parts.slice(1)?.join(", "),
                        Icon: mapSearchIcon((feature.place_type as string[])[0]),
                        iconSize: "small",
                        center: feature.center as LngLatCoordinatePair,
                    } satisfies LocationResult

                    return result
                }) || []
            )
        } catch (err) {
            console.error("Geocoding error:", err)
        }
    }, [])

    const searchW3W = useCallback(async (searchQuery: string) => {
        const center = await convertFromW3W(searchQuery)
        if (!center || latestQuery.current !== searchQuery) return

        setCoordinateResult({
            center,
            title: searchQuery,
            Icon: Icons.PinDrop,
            iconSize: "normal",
        })
    }, [])

    const handleSelect = useCallback(
        (place: LocationResult) => {
            abortController.current.abort("cancel")
            abortController.current = new AbortController()
            if (place.center) {
                dispatch(setFollowingEntity(undefined))
                setNewViewstate(
                    { latitude: place.center[1], longitude: place.center[0], zoom: 16 },
                    { disableFly: !!getGlobalTerrainData().terrainState }
                )

                dispatch(setSearchCoordinate(place.center as [number, number]))
                window.addEventListener("mousedown", () => dispatch(setSearchCoordinate()), {
                    once: true,
                })
            } else if (place.zoomTo) place.zoomTo()

            setCoordinateResult(undefined)
            setEntityResults([])
            setGeoResults([])
            dispatch(setMapSearchIsActive(false))
        },
        [dispatch]
    )

    const handleInputChange = useCallback(
        (input: string | undefined) => {
            setSearchContents(input)
            latestQuery.current = input

            if (!!input) {
                const coordinate = parseCoordinate(input)

                if (coordinate) {
                    const { latitude, longitude } = coordinate

                    setCoordinateResult({
                        center: [longitude, latitude],
                        title: `${latitude.toFixed(5)}°, ${longitude.toFixed(5)}°`,
                        Icon: Icons.PinDrop,
                        iconSize: "normal",
                    })
                } else {
                    setCoordinateResult(undefined)
                    if (w3wAddressPattern.test(input)) searchW3W(input)
                    setEntityResults(searchRegisteredEntities(input))
                    if (input.length > 2) searchGeocode(input)
                }
            } else {
                if (input === undefined) dispatch(setMapSearchIsActive(false))
                setCoordinateResult(undefined)
                setEntityResults([])
                setGeoResults([])
            }
        },
        [searchGeocode, searchW3W, dispatch]
    )

    const concatenatedResults = useMemo(
        () => [
            ...(!!coordinateResult
                ? [coordinateResult]
                : [
                      ...entityResults.slice(0, Math.max(3, 5 - geoResults.length)),
                      ...geoResults.slice(0, 5 - Math.min(2, entityResults.length)),
                  ]),
        ],
        [coordinateResult, entityResults, geoResults]
    )

    return { handleSelect, handleInputChange, searchContents, concatenatedResults }
}
